// Client for POST /retrieval/query — the live legal-source retrieval stream —
// plus the saved-address, history and bulk endpoints behind it.
//
// The streaming contract mirrors the backend orchestrator's event types:
// resolved → layer_start (all layers up-front, federal→local) → document (as
// each source completes) → layer_done → done; `error` only for
// orchestrator-level failures. SSE frames are hand-parsed off a fetch body so
// the Authorization header works (EventSource cannot send one).

import { API_BASE_URL, fetchWithAuth, getStoredToken } from "@/lib/api-client";
import type { JurisdictionLevel } from "@/lib/jurisdiction";

export type DocumentStatus =
  | "ok"
  | "unavailable"
  | "not_yet_supported"
  | "not_applicable"
  | "no_results";

export type Provenance = {
  curated?: boolean;
  source_kind?: string;
  verified_on?: string;
  identity_markers?: string[];
  content_hash?: string;
  effective_date?: string | null;
  expected_effective_date?: string | null;
  latest_legislation?: string | null;
  stale?: boolean;
};

export type Explanation = {
  citation?: string | null;
  key_passage?: string | null;
  explanation?: string | null;
};

export type RetrievalDocument = {
  status: DocumentStatus;
  source: string;
  jurisdiction_level: string;
  jurisdiction_name: string | null;
  citation: string | null;
  heading: string | null;
  text: string | null;
  url: string | null;
  retrieved_at: string | null;
  error: string | null;
  extra?: {
    provenance?: Provenance;
    explanation?: Explanation;
    [key: string]: unknown;
  };
};

export type LayerTag = {
  level: JurisdictionLevel | string;
  name: string | null;
  in_scope?: boolean;
  index: number;
};

export type RetrievalEvent =
  | { type: "resolved"; address?: string; normalized_address?: string; lat?: number; lng?: number }
  | { type: "layer_start"; layer: LayerTag }
  | { type: "document"; layer: LayerTag; document: RetrievalDocument }
  | { type: "layer_done"; layer: LayerTag }
  | { type: "error"; scope?: string; source?: string | null; message?: string }
  | { type: "done" };

export async function streamRetrieval(
  address: string,
  topic: string,
  {
    explain = true,
    recordHistory = true,
    onEvent,
    signal,
  }: {
    explain?: boolean;
    recordHistory?: boolean;
    onEvent: (event: RetrievalEvent) => void;
    signal?: AbortSignal;
  },
): Promise<void> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/retrieval/query`, {
    method: "POST",
    mode: "cors",
    cache: "no-store",
    headers,
    body: JSON.stringify({
      address,
      topic,
      stream: true,
      explain,
      record_history: recordHistory,
    }),
    signal,
  });
  if (!res.ok || !res.body) {
    if (res.status === 429) {
      throw new Error("Rate limit reached — please wait a minute and try again.");
    }
    throw new Error("The lookup service is unavailable right now. Try again shortly.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawDone = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice(6)) as RetrievalEvent;
          if (event.type === "done") sawDone = true;
          onEvent(event);
        } catch {
          // A malformed frame is dropped; the stream's `done` event still ends the run.
        }
      }
    }
  }
  // The server always terminates a completed stream with a `done` event. A
  // stream that ends without one was cut off in transit (proxy, network, tab
  // suspension) — surface that instead of leaving a partial stack rendered as
  // if it were the whole answer.
  if (!sawDone) {
    throw new StreamInterruptedError();
  }
}

/** Raised when the SSE stream ended before the server's terminal `done` event. */
export class StreamInterruptedError extends Error {
  constructor() {
    super(
      "The connection was interrupted before every layer finished loading, so " +
        "the results shown may be incomplete. Please run the lookup again.",
    );
    this.name = "StreamInterruptedError";
  }
}

export type AssembledLayer = LayerTag & { documents: RetrievalDocument[] };

export type RetrievalResult = {
  address?: string;
  normalized_address?: string;
  topic: string;
  layers: AssembledLayer[];
  errors: { message?: string }[];
};

export async function retrieveOnce(
  address: string,
  topic: string,
  {
    explain = true,
    recordHistory = true,
  }: { explain?: boolean; recordHistory?: boolean } = {},
): Promise<RetrievalResult> {
  const res = await fetchWithAuth("/retrieval/query", {
    method: "POST",
    body: JSON.stringify({
      address,
      topic,
      stream: false,
      explain,
      record_history: recordHistory,
    }),
  });
  if (!res.ok) {
    if (res.status === 429) {
      throw new Error("Rate limit reached — please wait a minute and try again.");
    }
    throw new Error("The lookup service is unavailable right now.");
  }
  return (await res.json()) as RetrievalResult;
}

// --- Saved addresses --------------------------------------------------------

export type SavedAddress = {
  id: number;
  address: string;
  normalized_address: string;
  label: string | null;
  topic: string;
  monitoring_enabled: boolean;
  created_at: string | null;
};

export type MonitorEventItem = {
  id: number;
  citation: string | null;
  change_type: string;
  detail: string | null;
  detected_at: string | null;
};

export async function listSavedAddresses(): Promise<{
  addresses: SavedAddress[];
  limit: number;
  tier: string;
}> {
  const res = await fetchWithAuth("/api/addresses");
  if (res.status === 403) throw new PlanGateError();
  if (!res.ok) throw new Error("Could not load saved addresses.");
  return res.json();
}

export async function saveAddress(
  address: string,
  topic: string,
  label?: string,
): Promise<SavedAddress> {
  const res = await fetchWithAuth("/api/addresses", {
    method: "POST",
    body: JSON.stringify({ address, topic, label: label || null }),
  });
  if (res.status === 403) throw new PlanGateError(await detailOf(res));
  if (res.status === 409) throw new Error("That address and topic is already saved.");
  if (!res.ok) throw new Error((await detailOf(res)) || "Could not save the address.");
  return res.json();
}

export async function updateSavedAddress(
  id: number,
  patch: { label?: string; monitoring_enabled?: boolean },
): Promise<SavedAddress> {
  const res = await fetchWithAuth(`/api/addresses/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error("Could not update the saved address.");
  return res.json();
}

export async function deleteSavedAddress(id: number): Promise<void> {
  const res = await fetchWithAuth(`/api/addresses/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Could not remove the saved address.");
}

export async function listMonitorEvents(id: number): Promise<MonitorEventItem[]> {
  const res = await fetchWithAuth(`/api/addresses/${id}/events`);
  if (!res.ok) throw new Error("Could not load change events.");
  const payload = await res.json();
  return payload.events as MonitorEventItem[];
}

// --- History & bulk ---------------------------------------------------------

export type HistoryItem = {
  id: number;
  address: string;
  topic: string;
  created_at: string | null;
};

export async function listHistory(): Promise<HistoryItem[]> {
  const res = await fetchWithAuth("/api/history");
  if (res.status === 403) throw new PlanGateError();
  if (!res.ok) throw new Error("Could not load lookup history.");
  const payload = await res.json();
  return payload.history as HistoryItem[];
}

export async function deleteHistory(id: number): Promise<void> {
  const res = await fetchWithAuth(`/api/history/${id}`, { method: "DELETE" });
  // 404 means it is already gone — the outcome the caller wanted.
  if (!res.ok && res.status !== 404) {
    throw new Error("Could not remove the lookup.");
  }
}

export type BulkRow = {
  address: string;
  ok: boolean;
  normalized_address?: string;
  layers?: { level: string; name: string; in_scope: boolean; source_url?: string | null }[];
  error?: string;
};

export async function bulkResolve(addresses: string[]): Promise<BulkRow[]> {
  const res = await fetchWithAuth("/api/addresses/bulk", {
    method: "POST",
    body: JSON.stringify({ addresses }),
  });
  if (res.status === 403) throw new PlanGateError();
  if (!res.ok) throw new Error((await detailOf(res)) || "Bulk lookup failed.");
  const payload = await res.json();
  return payload.results as BulkRow[];
}

/** Raised when the API says the caller's plan does not include a feature. */
export class PlanGateError extends Error {
  constructor(detail?: string | null) {
    super(detail || "This feature needs an active subscription.");
    this.name = "PlanGateError";
  }
}

async function detailOf(res: Response): Promise<string | null> {
  try {
    const payload = await res.json();
    return typeof payload.detail === "string" ? payload.detail : null;
  } catch {
    return null;
  }
}
